import { memo } from 'react';

import { TriangleAlertIcon } from '@air/design-icons';

import { WorkspaceAgentPanelConsentSummary } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/WorkspaceAgentPanelConsentSummary';
import { WorkspaceAgentTagChangesTable } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/WorkspaceAgentTagChangesTable';
import { WorkspaceAgentBeforeAfterTable } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/tables/WorkspaceAgentBeforeAfterTable';
import { type AgentElicitationDisplaySampleItem } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/elicitation';

const TRUNCATED_SAMPLE_CAPTION = 'Showing a sample of the full change.';

type DisplaySampleChange = Extract<AgentElicitationDisplaySampleItem, { type: 'change' }>;
type DisplaySampleLine = Extract<AgentElicitationDisplaySampleItem, { type: 'line' }>;
type DisplaySampleTags = Extract<AgentElicitationDisplaySampleItem, { type: 'tags' }>;

export type WorkspaceAgentPanelConsentDisplayProps = {
  instruction: string;
  sample: AgentElicitationDisplaySampleItem[] | undefined;
  summary: string | undefined;
  truncated: boolean;
};

const isDisplaySampleLine = (item: AgentElicitationDisplaySampleItem): item is DisplaySampleLine =>
  item.type === 'line';

const isDisplaySampleChange = (item: AgentElicitationDisplaySampleItem): item is DisplaySampleChange =>
  item.type === 'change';

const isDisplaySampleTags = (item: AgentElicitationDisplaySampleItem): item is DisplaySampleTags =>
  item.type === 'tags';

export const WorkspaceAgentPanelConsentDisplay = memo(
  ({ instruction, sample, summary, truncated }: WorkspaceAgentPanelConsentDisplayProps) => {
    const lineItems = sample?.filter(isDisplaySampleLine) ?? [];
    const changeRows = (sample?.filter(isDisplaySampleChange) ?? []).map((item) => ({
      after: item.after,
      before: item.before,
    }));
    const tagRows = sample?.filter(isDisplaySampleTags) ?? [];
    const hasTable = changeRows.length > 0 || tagRows.length > 0;
    const hasSampleContent = lineItems.length > 0 || hasTable;
    const truncatedCaption = truncated && hasSampleContent ? TRUNCATED_SAMPLE_CAPTION : undefined;
    const tableCaption = changeRows.length > 0 ? truncatedCaption : undefined;
    const tagTableCaption = changeRows.length === 0 ? truncatedCaption : undefined;
    const standaloneTruncatedCaption = hasTable ? undefined : truncatedCaption;

    return (
      <div className="flex flex-col gap-2" data-testid="WORKSPACE_AGENT_CONSENT_DISPLAY">
        <div data-testid="WORKSPACE_AGENT_CONSENT_DISPLAY_INSTRUCTION">
          <WorkspaceAgentPanelConsentSummary summary={instruction} />
        </div>
        {summary ? (
          <p
            className="inline-flex items-start gap-1 text-12 text-gray-9 leading-4"
            data-testid="WORKSPACE_AGENT_CONSENT_DISPLAY_SUMMARY"
          >
            <TriangleAlertIcon aria-hidden className="mt-0.5 size-3 shrink-0" />
            <span>{summary}</span>
          </p>
        ) : null}
        {lineItems.length > 0 ? (
          <ul className="flex flex-col gap-1" data-testid="WORKSPACE_AGENT_CONSENT_DISPLAY_LINES">
            {lineItems.map((item, index) => (
              <li
                className="text-12 text-gray-12 leading-4"
                data-testid="WORKSPACE_AGENT_CONSENT_DISPLAY_LINE"
                key={`line-${index}-${item.text}`}
              >
                {item.text}
              </li>
            ))}
          </ul>
        ) : null}
        <WorkspaceAgentBeforeAfterTable
          caption={tableCaption}
          data-testid="WORKSPACE_AGENT_CONSENT_DISPLAY_CHANGES"
          rows={changeRows}
        />
        <WorkspaceAgentTagChangesTable caption={tagTableCaption} rows={tagRows} />
        {standaloneTruncatedCaption ? (
          <p className="text-12 text-gray-9 leading-4" data-testid="WORKSPACE_AGENT_CONSENT_DISPLAY_TRUNCATED">
            {standaloneTruncatedCaption}
          </p>
        ) : null}
      </div>
    );
  },
);

WorkspaceAgentPanelConsentDisplay.displayName = 'WorkspaceAgentPanelConsentDisplay';
